import {
    PDFDocument,
    PDFRawStream,
    PDFName,
    PDFNumber,
    PDFArray,
    PDFDict,
    PDFRef,
    decodePDFRawStream,
} from 'pdf-lib';
import imageCompression from 'browser-image-compression';

/** Image quality in percent, plus the three on/off extras. */
export const DEFAULT_COMPRESS_PDF_SETTINGS = {
    quality: 75,
    removeMetadata: false,
    grayscale: false,
    compressStreams: true,
};

const MIN_IMAGE_DIM = 8;
const MIN_IMAGE_BYTES = 2048;
/** Below this, zlib's header and checksum outweigh anything deflate saves. */
const MIN_DEFLATE_BYTES = 256;

function getNumber(dict, key) {
    const value = dict.lookup(PDFName.of(key));
    return value instanceof PDFNumber ? value.asNumber() : null;
}

function nameOf(dict, key) {
    const value = dict.lookup(PDFName.of(key));
    return value instanceof PDFName ? value.asString() : null;
}

function getFilterNames(dict) {
    const filter = dict.lookup(PDFName.of('Filter'));
    if (!filter) return [];
    if (filter instanceof PDFName) return [filter.asString()];
    if (filter instanceof PDFArray) {
        const names = [];
        for (let i = 0; i < filter.size(); i++) {
            const entry = filter.lookup(i);
            if (entry instanceof PDFName) names.push(entry.asString());
        }
        return names;
    }
    return [];
}

function isJpegBytes(bytes) {
    return bytes.length > 2 && bytes[0] === 0xff && bytes[1] === 0xd8;
}

/**
 * The slider is image quality alone, but the former presets also capped the
 * longest side (52% → 1400px, 72% → 2000px, 85% → 2400px). 3000·q − 160 passes
 * through all three, so the same quality still gives the same file.
 */
function imageSettingsFor(settings) {
    const percent = Number(settings.quality) || DEFAULT_COMPRESS_PDF_SETTINGS.quality;
    const quality = Math.min(1, Math.max(0.1, percent / 100));
    return {
        quality,
        maxWidthOrHeight: Math.max(800, Math.round(3000 * quality - 160)),
        grayscale: !!settings.grayscale,
    };
}

async function loadPdf(file) {
    const bytes = new Uint8Array(await file.arrayBuffer());
    try {
        // updateMetadata: false — otherwise pdf-lib stamps its own Producer,
        // Creator and dates into every file, whichever way "remove metadata" is set.
        return await PDFDocument.load(bytes, { ignoreEncryption: true, updateMetadata: false });
    } catch (err) {
        if (String(err?.message || '').toLowerCase().includes('password')) {
            throw new Error('password');
        }
        throw err;
    }
}

/* ─── Colour spaces ─────────────────────────────────────────────────────── */

const DEVICE_KINDS = {
    '/DeviceGray': 'gray',
    '/CalGray': 'gray',
    '/G': 'gray',
    '/DeviceRGB': 'rgb',
    '/CalRGB': 'rgb',
    '/RGB': 'rgb',
    '/DeviceCMYK': 'cmyk',
    '/CMYK': 'cmyk',
};

/**
 * 'gray' | 'rgb' | 'cmyk' for plain device-like colour spaces; null for the
 * rest (indexed, separation, DeviceN, Lab, pattern), which are left alone.
 */
function colorSpaceKind(context, value) {
    const space = context.lookup(value);
    if (space instanceof PDFName) return DEVICE_KINDS[space.asString()] ?? null;
    if (!(space instanceof PDFArray) || space.size() === 0) return null;

    const family = space.lookup(0);
    if (!(family instanceof PDFName)) return null;
    const name = family.asString();
    if (name === '/CalGray') return 'gray';
    if (name === '/CalRGB') return 'rgb';
    if (name === '/ICCBased') {
        const profile = space.lookup(1);
        const n = profile instanceof PDFRawStream ? getNumber(profile.dict, 'N') : null;
        return n === 1 ? 'gray' : n === 3 ? 'rgb' : n === 4 ? 'cmyk' : null;
    }
    return null;
}

function rgbToGray([r, g, b]) {
    return Math.min(1, Math.max(0, 0.299 * r + 0.587 * g + 0.114 * b));
}

function cmykToGray([c, m, y, k]) {
    return Math.min(1, Math.max(0, (1 - k) * (1 - (0.299 * c + 0.587 * m + 0.114 * y))));
}

/* ─── Images ────────────────────────────────────────────────────────────── */

function grayscaleCanvas(canvas) {
    const ctx = canvas.getContext('2d');
    const image = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const px = image.data;
    for (let i = 0; i < px.length; i += 4) {
        const y = Math.round(0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2]);
        px[i] = y;
        px[i + 1] = y;
        px[i + 2] = y;
    }
    ctx.putImageData(image, 0, 0);
}

async function canvasToJpegBlob(canvas, settings) {
    if (settings.grayscale) grayscaleCanvas(canvas);
    const blob = await new Promise((resolve, reject) => {
        canvas.toBlob((result) => (result ? resolve(result) : reject(new Error('encode failed'))), 'image/jpeg', settings.quality);
    });
    const file = new File([blob], 'image.jpg', { type: 'image/jpeg' });
    return imageCompression(file, {
        maxSizeMB: 50,
        maxWidthOrHeight: Math.max(canvas.width, canvas.height),
        useWebWorker: true,
        initialQuality: settings.quality,
        fileType: 'image/jpeg',
    });
}

function scaledToFit(source, width, height, settings) {
    const longest = Math.max(width, height);
    const scale = longest > settings.maxWidthOrHeight ? settings.maxWidthOrHeight / longest : 1;
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));
    const ctx = canvas.getContext('2d', { alpha: false });
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
    return canvas;
}

/** Raw 8-bit gray or RGB samples, laid over white where a soft mask says so. */
function samplesToCanvas(samples, width, height, channels, alpha) {
    const pixels = width * height;
    if (samples.length < pixels * channels) return null;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    const image = ctx.createImageData(width, height);
    const px = image.data;

    for (let i = 0; i < pixels; i++) {
        const s = i * channels;
        const r = samples[s];
        const g = channels === 3 ? samples[s + 1] : r;
        const b = channels === 3 ? samples[s + 2] : r;
        const o = alpha ? alpha[i] / 255 : 1;
        const j = i * 4;
        px[j] = Math.round(r * o + 255 * (1 - o));
        px[j + 1] = Math.round(g * o + 255 * (1 - o));
        px[j + 2] = Math.round(b * o + 255 * (1 - o));
        px[j + 3] = 255;
    }

    ctx.putImageData(image, 0, 0);
    return canvas;
}

/** Soft-mask alpha, only when it lines up pixel for pixel with its image. */
function decodeMaskAlpha(smaskStream, width, height) {
    if (getNumber(smaskStream.dict, 'Width') !== width || getNumber(smaskStream.dict, 'Height') !== height) return null;
    if (getNumber(smaskStream.dict, 'BitsPerComponent') !== 8) return null;

    const decoded = decodePDFRawStream(smaskStream).decode();
    return decoded.length >= width * height ? decoded : null;
}

async function rawStreamToJpeg(context, stream, settings) {
    const dict = stream.dict;
    const width = getNumber(dict, 'Width');
    const height = getNumber(dict, 'Height');
    if (!width || !height || width < MIN_IMAGE_DIM || height < MIN_IMAGE_DIM) return null;

    const filters = getFilterNames(dict);
    if (filters.includes('/JPXDecode')) return null;

    const smask = dict.lookup(PDFName.of('SMask'));
    const smaskStream = smask instanceof PDFRawStream ? smask : null;
    // The mask is flattened onto white; one we cannot read would otherwise be
    // dropped and turn transparent areas into whatever colour hides under them.
    const alpha = smaskStream ? decodeMaskAlpha(smaskStream, width, height) : null;
    if (smaskStream && !alpha) return null;

    if (filters.includes('/DCTDecode') || isJpegBytes(stream.contents)) {
        const bitmap = await createImageBitmap(new Blob([stream.contents], { type: 'image/jpeg' }));
        let source = bitmap;
        if (alpha) {
            const full = scaledToFit(bitmap, width, height, { maxWidthOrHeight: Infinity });
            const ctx = full.getContext('2d');
            const image = ctx.getImageData(0, 0, width, height);
            for (let i = 0, j = 0; i < width * height; i++, j += 4) {
                const o = alpha[i] / 255;
                image.data[j] = Math.round(image.data[j] * o + 255 * (1 - o));
                image.data[j + 1] = Math.round(image.data[j + 1] * o + 255 * (1 - o));
                image.data[j + 2] = Math.round(image.data[j + 2] * o + 255 * (1 - o));
            }
            ctx.putImageData(image, 0, 0);
            source = full;
        }
        const canvas = scaledToFit(source, width, height, settings);
        bitmap.close();
        return canvasToJpegBlob(canvas, settings);
    }

    // Only samples a canvas can take as they are: 8-bit gray or RGB with no
    // Decode remap. Indexed images, for one, are palette numbers — read as gray
    // they came out as noise.
    const kind = colorSpaceKind(context, dict.get(PDFName.of('ColorSpace')));
    if ((kind !== 'gray' && kind !== 'rgb') || getNumber(dict, 'BitsPerComponent') !== 8 || dict.has(PDFName.of('Decode'))) {
        return null;
    }

    const samples = decodePDFRawStream(stream).decode();
    const full = samplesToCanvas(samples, width, height, kind === 'rgb' ? 3 : 1, alpha);
    if (!full) return null;
    return canvasToJpegBlob(scaledToFit(full, width, height, settings), settings);
}

function isImageStream(object) {
    if (!(object instanceof PDFRawStream)) return false;
    const dict = object.dict;
    if (nameOf(dict, 'Subtype') !== '/Image') return false;
    if (dict.lookup(PDFName.of('ImageMask'))) return false;
    return true;
}

function streamKey(context, stream) {
    const ref = context.getObjectRef?.(stream);
    return ref?.tag ?? stream;
}

/**
 * Soft masks are image streams too, but they must stay gray and unencoded as
 * JPEG — compressing one as a picture wrote an RGB mask viewers reject.
 * @returns {Map<string, { ref: PDFRef, users: number }>}
 */
function collectSoftMasks(context) {
    const masks = new Map();
    for (const [, object] of context.enumerateIndirectObjects()) {
        if (!(object instanceof PDFRawStream) || nameOf(object.dict, 'Subtype') !== '/Image') continue;
        const ref = object.dict.get(PDFName.of('SMask'));
        if (!(ref instanceof PDFRef)) continue;
        const entry = masks.get(ref.tag) || { ref, users: 0 };
        entry.users += 1;
        masks.set(ref.tag, entry);
    }
    return masks;
}

function collectPageImageStreams(context, page) {
    const streams = [];
    const resources = page.node.Resources();
    const xObjects = resources?.lookupMaybe(PDFName.of('XObject'), PDFDict);
    if (!xObjects) return streams;

    for (const [, value] of xObjects.entries()) {
        const object = context.lookup(value);
        if (isImageStream(object)) streams.push(object);
    }
    return streams;
}

/**
 * Group image streams by page so progress can be reported per page.
 * A stream shared across pages is attributed to the first page that uses it,
 * so each image is only compressed once.
 */
function collectImageStreamsByPage(pdfDoc, softMasks) {
    const { context } = pdfDoc;
    const pages = pdfDoc.getPages();
    const perPage = [];
    const processedKeys = new Set(softMasks.keys());

    for (const page of pages) {
        const pageStreams = [];
        for (const stream of collectPageImageStreams(context, page)) {
            const key = streamKey(context, stream);
            if (processedKeys.has(key)) continue;
            processedKeys.add(key);
            pageStreams.push(stream);
        }
        perPage.push(pageStreams);
    }

    // Images not placed on a page directly (inside forms, annotations) go to the last page bucket.
    const orphans = [];
    for (const [ref, object] of context.enumerateIndirectObjects()) {
        if (!isImageStream(object) || processedKeys.has(ref.tag)) continue;
        processedKeys.add(ref.tag);
        orphans.push(object);
    }
    if (orphans.length) {
        if (perPage.length === 0) perPage.push(orphans);
        else perPage[perPage.length - 1].push(...orphans);
    }

    return perPage;
}

function applyJpegToImageStream(stream, jpegBytes) {
    const droppedMask = stream.dict.get(PDFName.of('SMask'));
    stream.contents = jpegBytes;
    stream.dict.set(PDFName.of('Filter'), PDFName.of('DCTDecode'));
    stream.dict.set(PDFName.of('Length'), PDFNumber.of(jpegBytes.length));
    // Canvas JPEG output is always RGB — update metadata so viewers decode correctly.
    stream.dict.set(PDFName.of('ColorSpace'), PDFName.of('DeviceRGB'));
    stream.dict.set(PDFName.of('BitsPerComponent'), PDFNumber.of(8));
    stream.dict.delete(PDFName.of('DecodeParms'));
    stream.dict.delete(PDFName.of('SMask'));
    stream.dict.delete(PDFName.of('Intent'));
    stream.dict.delete(PDFName.of('Matte'));
    stream.dict.delete(PDFName.of('Decode'));
    return droppedMask instanceof PDFRef ? droppedMask : null;
}

async function compressImageStream(context, stream, settings, softMasks) {
    // Grayscale has to reach every colour picture, even ones too small or too
    // well packed to shrink; gray ones follow the usual size rules.
    const recolor = settings.grayscale
        && colorSpaceKind(context, stream.dict.get(PDFName.of('ColorSpace'))) !== 'gray';
    if (!recolor && stream.contents.length < MIN_IMAGE_BYTES) return false;

    let jpegBlob;
    try {
        jpegBlob = await rawStreamToJpeg(context, stream, settings);
    } catch {
        return false;
    }
    if (!jpegBlob) return false;

    const jpegBytes = new Uint8Array(await jpegBlob.arrayBuffer());
    if (!recolor && jpegBytes.length >= stream.contents.length * 0.97) return false;

    const droppedMask = applyJpegToImageStream(stream, jpegBytes);
    // The mask is baked in now; once no image points at it, stop writing it out.
    const entry = droppedMask && softMasks.get(droppedMask.tag);
    if (entry && --entry.users === 0) context.delete(entry.ref);
    return true;
}

async function compressPagesImages(context, pagesStreams, settings, softMasks, onProgress) {
    let changed = 0;
    const pageTotal = pagesStreams.length;

    for (let pageIndex = 0; pageIndex < pageTotal; pageIndex++) {
        onProgress?.(pageIndex + 1, pageTotal);
        for (const stream of pagesStreams[pageIndex]) {
            if (await compressImageStream(context, stream, settings, softMasks)) changed += 1;
        }
    }

    return changed;
}

/* ─── Streams ───────────────────────────────────────────────────────────── */

const canDeflate = typeof CompressionStream !== 'undefined';

/** zlib-wrapped deflate — exactly what /FlateDecode reads. */
async function deflateBytes(bytes) {
    const packed = new Blob([bytes]).stream().pipeThrough(new CompressionStream('deflate'));
    return new Uint8Array(await new Response(packed).arrayBuffer());
}

async function setStreamBytes(stream, bytes, compress) {
    const packed = compress && canDeflate && bytes.length >= MIN_DEFLATE_BYTES ? await deflateBytes(bytes) : null;
    const usePacked = packed && packed.length < bytes.length;
    stream.contents = usePacked ? packed : bytes;
    if (usePacked) stream.dict.set(PDFName.of('Filter'), PDFName.of('FlateDecode'));
    else stream.dict.delete(PDFName.of('Filter'));
    stream.dict.delete(PDFName.of('DecodeParms'));
    stream.dict.set(PDFName.of('Length'), PDFNumber.of(stream.contents.length));
}

/** Deflate every stream still stored as plain bytes. */
async function deflateLooseStreams(context) {
    if (!canDeflate) return;
    for (const [, object] of context.enumerateIndirectObjects()) {
        if (!(object instanceof PDFRawStream)) continue;
        const { dict } = object;
        if (dict.has(PDFName.of('Filter')) || dict.has(PDFName.of('DecodeParms'))) continue;
        if (object.contents.length < MIN_DEFLATE_BYTES) continue;
        // XMP stays plain text, as the PDF spec asks, so file managers can read it.
        if (nameOf(dict, 'Type') === '/Metadata') continue;
        await setStreamBytes(object, object.contents, true);
    }
}

/* ─── Metadata ──────────────────────────────────────────────────────────── */

/** Drop the document info dictionary and every XMP packet (document, page, image). */
function stripMetadata(context) {
    const info = context.trailerInfo.Info;
    if (info instanceof PDFRef) context.delete(info);
    context.trailerInfo.Info = undefined;

    for (const [, object] of context.enumerateIndirectObjects()) {
        const dict = object instanceof PDFDict ? object : object instanceof PDFRawStream ? object.dict : null;
        const metadata = dict?.get(PDFName.of('Metadata'));
        if (!metadata) continue;
        dict.delete(PDFName.of('Metadata'));
        if (metadata instanceof PDFRef) context.delete(metadata);
    }
}

/* ─── Grayscale vector content ──────────────────────────────────────────── */

const isWhite = (b) => b === 0x20 || b === 0x0a || b === 0x0d || b === 0x09 || b === 0x0c || b === 0x00;
const isDelimiter = (b) => b === 0x28 || b === 0x29 || b === 0x3c || b === 0x3e || b === 0x5b
    || b === 0x5d || b === 0x7b || b === 0x7d || b === 0x2f || b === 0x25;
const NUMBER_RE = /^[+-]?(\d+\.?\d*|\.\d+)$/;
const ascii = (text) => Uint8Array.from(text, (c) => c.charCodeAt(0));
const latin1 = (bytes, start, end) => String.fromCharCode(...bytes.subarray(start, end));

/** One content-stream token; strings, comments and names are skipped whole. */
function nextToken(bytes, from) {
    const len = bytes.length;
    let i = from;
    for (;;) {
        while (i < len && isWhite(bytes[i])) i++;
        if (i >= len) return null;
        if (bytes[i] !== 0x25) break;
        while (i < len && bytes[i] !== 0x0a && bytes[i] !== 0x0d) i++;
    }

    const start = i;
    const b = bytes[i];
    if (b === 0x28) {
        let depth = 0;
        for (; i < len; i++) {
            if (bytes[i] === 0x5c) { i++; continue; }
            if (bytes[i] === 0x28) depth++;
            else if (bytes[i] === 0x29 && --depth === 0) { i++; break; }
        }
        return { type: 'string', start, end: Math.min(i, len) };
    }
    if (b === 0x3c) {
        if (bytes[i + 1] === 0x3c) return { type: 'open', start, end: i + 2 };
        while (i < len && bytes[i] !== 0x3e) i++;
        return { type: 'string', start, end: Math.min(i + 1, len) };
    }
    if (b === 0x3e) {
        return bytes[i + 1] === 0x3e ? { type: 'close', start, end: i + 2 } : { type: 'other', start, end: i + 1 };
    }
    if (b === 0x5b) return { type: 'open', start, end: i + 1 };
    if (b === 0x5d) return { type: 'close', start, end: i + 1 };
    if (b === 0x7b || b === 0x7d || b === 0x29) return { type: 'other', start, end: i + 1 };

    const isName = b === 0x2f;
    if (isName) i++;
    while (i < len && !isWhite(bytes[i]) && !isDelimiter(bytes[i])) i++;
    // No operator or colour-space name runs this long; a run of stray binary does.
    if (i - start > 127) return { type: 'other', start, end: i };
    const word = latin1(bytes, start, i);
    if (isName) return { type: 'name', start, end: i, word };
    if (NUMBER_RE.test(word)) return { type: 'number', start, end: i, word };
    if (word === 'true' || word === 'false' || word === 'null') return { type: 'other', start, end: i };
    return { type: 'op', start, end: i, word };
}

/** Where the `EI` after an inline image's binary data begins. */
function inlineImageEnd(bytes, dataStart) {
    for (let i = dataStart + 1; i < bytes.length - 1; i++) {
        if (bytes[i] === 0x45 && bytes[i + 1] === 0x49 && isWhite(bytes[i - 1])
            && (i + 2 >= bytes.length || isWhite(bytes[i + 2]) || isDelimiter(bytes[i + 2]))) {
            return i;
        }
    }
    return bytes.length;
}

const formatGray = (value) => String(Number(value.toFixed(4)));

/**
 * Rewrite the colour operators of one content stream to gray, splicing the
 * replacements into the original bytes so text, paths and inline data stay
 * byte-for-byte as they were.
 *
 * Fill and stroke colour spaces are tracked through q/Q: 'rgb' / 'cmyk' mean a
 * space already switched to DeviceGray whose sc operands still need mapping,
 * 'inherit' is a form's unknown starting space (mapped by operand count, since
 * the caller's space was converted too), null is a space left untouched.
 *
 * @returns {Uint8Array|null} null when nothing changed
 */
function grayscaleContentBytes(bytes, resolveKind, initialKind) {
    const out = [];
    let copied = 0;
    const replace = (start, end, text) => {
        out.push(bytes.subarray(copied, start), ascii(text));
        copied = end;
    };

    let fill = initialKind;
    let stroke = initialKind;
    const saved = [];
    let operands = [];
    let depth = 0;
    let compositeStart = 0;
    let pos = 0;

    for (let token = nextToken(bytes, 0); token; token = nextToken(bytes, pos)) {
        pos = token.end;
        if (token.type === 'open') {
            if (depth++ === 0) compositeStart = token.start;
            continue;
        }
        if (token.type === 'close') {
            if (depth > 0 && --depth === 0) operands.push({ type: 'other', start: compositeStart, end: token.end });
            continue;
        }
        if (depth > 0) continue;
        if (token.type !== 'op') {
            operands.push(token);
            continue;
        }

        const op = token.word;
        const nums = operands.every((o) => o.type === 'number') ? operands.map((o) => parseFloat(o.word)) : null;
        const from = operands.length ? operands[0].start : token.start;

        switch (op) {
            case 'q':
                saved.push([fill, stroke]);
                break;
            case 'Q':
                if (saved.length) [fill, stroke] = saved.pop();
                break;
            case 'g':
                fill = 'gray';
                break;
            case 'G':
                stroke = 'gray';
                break;
            case 'rg':
            case 'RG':
                if (nums?.length === 3) replace(from, token.end, `${formatGray(rgbToGray(nums))} ${op === 'rg' ? 'g' : 'G'}`);
                if (op === 'rg') fill = 'gray'; else stroke = 'gray';
                break;
            case 'k':
            case 'K':
                if (nums?.length === 4) replace(from, token.end, `${formatGray(cmykToGray(nums))} ${op === 'k' ? 'g' : 'G'}`);
                if (op === 'k') fill = 'gray'; else stroke = 'gray';
                break;
            case 'cs':
            case 'CS': {
                const name = operands.length === 1 && operands[0].type === 'name' ? operands[0].word : null;
                const kind = name ? resolveKind(name) : null;
                if (kind === 'rgb' || kind === 'cmyk') replace(from, token.end, `/DeviceGray ${op}`);
                if (op === 'cs') fill = kind; else stroke = kind;
                break;
            }
            case 'sc':
            case 'scn':
            case 'SC':
            case 'SCN': {
                const kind = op[0] === 's' ? fill : stroke;
                const count = nums?.length ?? 0;
                const from3 = count === 3 && (kind === 'rgb' || kind === 'inherit');
                const from4 = count === 4 && (kind === 'cmyk' || kind === 'inherit');
                if (from3 || from4) replace(from, token.end, `${formatGray(from3 ? rgbToGray(nums) : cmykToGray(nums))} ${op}`);
                break;
            }
            case 'ID':
                pos = inlineImageEnd(bytes, token.end);
                break;
            default:
                break;
        }
        operands = [];
    }

    if (!out.length) return null;
    out.push(bytes.subarray(copied));
    const result = new Uint8Array(out.reduce((sum, part) => sum + part.length, 0));
    let offset = 0;
    for (const part of out) {
        result.set(part, offset);
        offset += part.length;
    }
    return result;
}

function kindResolver(context, resources) {
    const spaces = resources instanceof PDFDict ? resources.lookup(PDFName.of('ColorSpace')) : null;
    return (name) => {
        if (DEVICE_KINDS[name] && name.startsWith('/Device')) return DEVICE_KINDS[name];
        if (!(spaces instanceof PDFDict)) return null;
        return colorSpaceKind(context, spaces.get(PDFName.of(name.slice(1))));
    };
}

/**
 * Gray every page, form XObject (annotation appearances included) and tiling
 * pattern. Shading gradients keep their colours.
 */
async function grayscaleVectorContent(pdfDoc, compress) {
    const { context } = pdfDoc;
    const jobs = new Map();

    for (const page of pdfDoc.getPages()) {
        const contents = page.node.Contents();
        const resources = page.node.Resources();
        const streams = contents instanceof PDFArray
            ? contents.asArray().map((item) => context.lookup(item))
            : [contents];
        // A page split over several streams shares one graphics state, so
        // every stream after the first starts from whatever came before.
        streams.forEach((stream, index) => {
            if (stream instanceof PDFRawStream && !jobs.has(stream)) {
                jobs.set(stream, { resources, initial: index === 0 ? 'gray' : 'inherit' });
            }
        });
    }

    for (const [, object] of context.enumerateIndirectObjects()) {
        if (!(object instanceof PDFRawStream) || jobs.has(object)) continue;
        const isForm = nameOf(object.dict, 'Subtype') === '/Form';
        const isTiling = getNumber(object.dict, 'PatternType') === 1;
        if (!isForm && !isTiling) continue;
        jobs.set(object, { resources: object.dict.lookup(PDFName.of('Resources')), initial: 'inherit' });
    }

    for (const [stream, { resources, initial }] of jobs) {
        let bytes;
        try {
            bytes = decodePDFRawStream(stream).decode();
        } catch {
            continue;
        }
        const rewritten = grayscaleContentBytes(bytes, kindResolver(context, resources), initial);
        if (rewritten) await setStreamBytes(stream, rewritten, compress || stream.dict.has(PDFName.of('Filter')));
    }
}

/* ─── Entry point ───────────────────────────────────────────────────────── */

/**
 * Compress a PDF: re-encode embedded images at the chosen quality (text and
 * vectors stay sharp), and optionally strip metadata, turn everything gray,
 * and deflate streams stored uncompressed.
 * @param {File|Blob} file
 * @param {typeof DEFAULT_COMPRESS_PDF_SETTINGS} settings
 * @param {(page: number, pageTotal: number) => void} [onProgress]
 */
export async function compressPdfFile(file, settings = DEFAULT_COMPRESS_PDF_SETTINGS, onProgress) {
    const options = { ...DEFAULT_COMPRESS_PDF_SETTINGS, ...settings };
    const pdfDoc = await loadPdf(file);
    const { context } = pdfDoc;

    if (options.removeMetadata) stripMetadata(context);

    const softMasks = collectSoftMasks(context);
    const pagesStreams = collectImageStreamsByPage(pdfDoc, softMasks);
    await compressPagesImages(context, pagesStreams, imageSettingsFor(options), softMasks, onProgress);

    if (options.grayscale) await grayscaleVectorContent(pdfDoc, options.compressStreams);
    if (options.compressStreams) await deflateLooseStreams(context);

    const bytes = await pdfDoc.save({ useObjectStreams: options.compressStreams });
    return new Blob([bytes], { type: 'application/pdf' });
}

export function formatFileSize(bytes) {
    if (!bytes || bytes < 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unit = 0;
    while (size >= 1024 && unit < units.length - 1) {
        size /= 1024;
        unit += 1;
    }
    const value = unit === 0 ? String(Math.round(size)) : size.toFixed(size >= 10 ? 1 : 2);
    return `${value} ${units[unit]}`;
}
